import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Clock, Calendar, Edit, Trash2, Plus, Search, ClipboardList, 
  Archive, Send, Download, Share2, Menu, BarChart2, 
  GraduationCap, MessageCircle, FileText, Star, Layout,
  AlertCircle, CheckCircle2, XCircle, Clock4, CalendarRange
} from 'lucide-react';
import CreateSurveyModal from '../components/CreateSurveyModal';
import AnalyticsModal from '../components/SurveyAnalyticsModal';

const DeleteConfirmModal = ({ isOpen, onClose, onConfirm }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center"
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Survey</h3>
          <p className="text-gray-600 mb-6">
            Are you sure you want to delete this survey? This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
            >
              Delete
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);


const SurveyCard = ({ survey, onDelete, onEdit, onViewAnalytics, onDuplicate, onArchive, onExport, onShare }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const theme = survey.settings?.theme || '#2563eb';

  const getStatusConfig = () => {
    // If no dates are set, survey is active by default
    if (!survey.schedule?.startDate && !survey.schedule?.endDate) {
      return {
        label: 'Active',
        color: 'emerald',
        icon: CheckCircle2,
        bgColor: 'bg-emerald-50',
        textColor: 'text-emerald-700',
        borderColor: 'border-emerald-200',
        gradient: 'from-emerald-500/20 to-transparent'
      };
    }

    const now = new Date();
    const startDate = survey.schedule?.startDate ? new Date(survey.schedule.startDate) : null;
    const endDate = survey.schedule?.endDate ? new Date(survey.schedule.endDate) : null;

    if (startDate && now < startDate) {
      return {
        label: 'Scheduled',
        color: 'blue',
        icon: CalendarRange,
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-700',
        borderColor: 'border-blue-200',
        gradient: 'from-blue-500/20 to-transparent'
      };
    }

    if (endDate && now > endDate) {
      return {
        label: 'Closed',
        color: 'rose',
        icon: Archive,
        bgColor: 'bg-rose-50',
        textColor: 'text-rose-700',
        borderColor: 'border-rose-200',
        gradient: 'from-rose-500/20 to-transparent'
      };
    }

    return {
      label: 'Active',
      color: 'emerald',
      icon: CheckCircle2,
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-700',
      borderColor: 'border-emerald-200',
      gradient: 'from-emerald-500/20 to-transparent'
    };
  };

  const status = getStatusConfig();
  const StatusIcon = status.icon;

  const getCategoryIcon = () => {
    switch(survey.category) {
      case 'general': return MessageCircle;
      case 'academic': return FileText;
      case 'feedback': return Star;
      case 'event': return Calendar;
      default: return Layout;
    }
  };

  const CategoryIcon = getCategoryIcon();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="group relative overflow-hidden bg-white rounded-2xl border-2 
        transition-all duration-300"
      style={{ borderColor: isHovered ? theme : 'rgb(229 231 235)' }}
    >
      {/* Premium Background Patterns */}
      <div className="absolute inset-0 transition-all duration-500 opacity-0 group-hover:opacity-100">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-blue-50/20" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.04),transparent_50%)]" />
        <div className="absolute inset-0" style={{ 
          backgroundImage: `radial-gradient(circle at 50% 50%, ${theme}08, transparent 70%)`
        }} />
      </div>

      {/* Card Content */}
      <div className="relative p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center space-x-4">
            <motion.div
              whileHover={{ scale: 1.1 }}
              className="relative group/icon"
            >
              <div className="absolute -inset-2 rounded-lg opacity-0 group-hover/icon:opacity-100 
                transition-all duration-300" style={{ background: `${theme}15` }} />
              <div className="relative p-2 rounded-lg" style={{ color: theme }}>
                <CategoryIcon className="w-6 h-6" />
              </div>
            </motion.div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-1 line-clamp-1"
                style={{ color: isHovered ? theme : undefined }}>
                {survey.title}
              </h3>
              <div className="flex items-center gap-2">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2
                    ${status.bgColor} ${status.textColor} ${status.borderColor} border`}
                >
                  <StatusIcon className="w-4 h-4" />
                  <span>{status.label}</span>
                </motion.div>
                <span className="text-sm text-gray-500">
                  {new Date(survey.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-600 mb-6 line-clamp-2">
          {survey.description || 'No description provided'}
        </p>

       
        {/* Schedule Info */}
        {(survey.schedule?.startDate || survey.schedule?.endDate) && (
          <div className="mb-6 p-3 rounded-xl bg-gray-50 border border-gray-100">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {survey.schedule.startDate && (
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span>Starts: {new Date(survey.schedule.startDate).toLocaleDateString()}</span>
                </div>
              )}
              {survey.schedule.endDate && (
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span>Ends: {new Date(survey.schedule.endDate).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between">
          <div className="flex space-x-1">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onEdit}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              style={{ color: theme }}
            >
              <Edit className="w-5 h-5" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onViewAnalytics}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              style={{ color: theme }}
            >
              <BarChart2 className="w-5 h-5" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onDelete}
              className="p-2 rounded-lg hover:bg-red-100 text-red-500 transition-colors"
            >
              <Trash2 className="w-5 h-5" />
            </motion.button>
          </div>

         
        </div>
      </div>
    </motion.div>
  );
};




const SurveyManagement = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    search: ''
  });

  useEffect(() => {
    fetchSurveys();
  }, [filters]);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      if (filters.status !== 'all') queryParams.append('status', filters.status);
      if (filters.category !== 'all') queryParams.append('category', filters.category);
      if (filters.search) queryParams.append('search', filters.search);

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/surveys/surveys?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch surveys');
      }

      const data = await response.json();
      setSurveys(data);
    } catch (error) {
      setError('Failed to load surveys. Please try again.');
      console.error('Error fetching surveys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/surveys/surveys/${selectedSurvey._id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to delete survey');

      setSurveys(surveys.filter(s => s._id !== selectedSurvey._id));
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting survey:', error);
      // Show error message to user
    }
  };

  const handleDuplicate = async (survey) => {
    try {
      const { _id, createdAt, updatedAt, ...surveyData } = survey;
      surveyData.title = `${surveyData.title} (Copy)`;

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/surveys/surveys`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(surveyData)
        }
      );

      if (!response.ok) throw new Error('Failed to duplicate survey');

      const newSurvey = await response.json();
      setSurveys([newSurvey, ...surveys]);
    } catch (error) {
      console.error('Error duplicating survey:', error);
      // Show error message to user
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Surveys & Feedback</h1>
              <p className="mt-1 text-sm text-gray-500">
                Create and manage surveys, polls, and feedback forms
              </p>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setSelectedSurvey(null);
                setShowCreateModal(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent 
                rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 
                hover:bg-blue-700 focus:outline-none"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Survey
            </motion.button>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search surveys..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg 
                  focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 
                focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
              <option value="archived">Archived</option>
            </select>

            

            <select
              value={filters.sort}
              onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 
                focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="responses">Most Responses</option>
              <option value="completion">Highest Completion</option>
            </select>
          </div>
        </div>
      </div>

      {/* Surveys Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {surveys.map((survey) => (
              <SurveyCard
                key={survey._id}
                survey={survey}
                onDelete={() => {
                  setSelectedSurvey(survey);
                  setShowDeleteModal(true);
                }}
                onEdit={() => {
                  setSelectedSurvey(survey);
                  setShowCreateModal(true);
                }}
                onViewAnalytics={() => {
                  setSelectedSurvey(survey);
                  setShowAnalyticsModal(true);
                }}
                onDuplicate={() => handleDuplicate(survey)}
                onArchive={async () => {
                  try {
                    const response = await fetch(
                      `${process.env.REACT_APP_BASE_URL}/api/surveys/surveys/${survey._id}`,
                      {
                        method: 'PUT',
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('token')}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ status: 'archived' })
                      }
                    );

                    if (!response.ok) throw new Error('Failed to archive survey');

                    const updatedSurvey = await response.json();
                    setSurveys(surveys.map(s => 
                      s._id === updatedSurvey._id ? updatedSurvey : s
                    ));
                  } catch (error) {
                    console.error('Error archiving survey:', error);
                  }
                }}
                onExport={() => {
                  // Handle export functionality
                  // This could download survey data in CSV/Excel format
                }}
                onShare={() => {
                  // Handle share functionality
                  // This could copy survey link or show sharing options
                }}
              />
            ))}
          </div>
        )}

        {!loading && surveys.length === 0 && (
          <div className="text-center py-12">
            <ClipboardList className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No surveys found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new survey.
            </p>
            <div className="mt-6">
              <button
                onClick={() => {
                  setSelectedSurvey(null);
                  setShowCreateModal(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent 
                  rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 
                  hover:bg-blue-700 focus:outline-none"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create Survey
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateSurveyModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        selectedSurvey={selectedSurvey}
        onSuccess={() => {
          setShowCreateModal(false);
          fetchSurveys();
        }}
      />

      <AnalyticsModal
        isOpen={showAnalyticsModal}
        onClose={() => setShowAnalyticsModal(false)}
        survey={selectedSurvey}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default SurveyManagement;